import {initContract} from '@ts-rest/core';
import {z} from 'zod';
import {DB_SCHEMAS} from '../db/schemaExports';
import {paginationQuerySchema, paginatedResponseSchema} from '../utils/pagination';
import {chatSourceSchema} from '../utils/chatSources';
import {chatAttachmentResponseSchema} from './chatAttachments';

const c = initContract();

export const chatConversationsContract = c.router(
  {
    create: {
      method: 'POST',
      path: '/',
      body: DB_SCHEMAS.createChatConversation.pick({title: true, assistantId: true, tenantId: true}),
      responses: {
        201: DB_SCHEMAS.selectChatConversation,
        400: z.object({
          message: z.string()
        }),
        403: z.object({
          message: z.string()
        })
      },
      summary: 'Create a chat conversation',
      description: 'Create a chat conversation'
    },
    list: {
      method: 'GET',
      path: '/',
      query: paginationQuerySchema.extend({
        sortBy: z.enum(['title', 'createdAt', 'updatedAt']).optional()
      }),
      responses: {
        200: paginatedResponseSchema(DB_SCHEMAS.selectChatConversation)
      }
    },
    get: {
      method: 'GET',
      path: '/:id',
      pathParams: DB_SCHEMAS.selectChatConversation.pick({id: true}),
      responses: {
        200: z.object({
          ...DB_SCHEMAS.selectChatConversation.shape,
          messages: z.array(
            z.discriminatedUnion('role', [
              z.object({
                role: z.literal('user'),
                content: z.string().describe('The content of the message'),
                attachments: z
                  .array(chatAttachmentResponseSchema)
                  .optional()
                  .describe('Attachments associated with this user message')
              }),
              z.object({
                role: z.literal('assistant'),
                content: z.string().describe('Concatenated text-block content (visible answer)'),
                blocks: z
                  .array(
                    z.discriminatedUnion('kind', [
                      z.object({kind: z.literal('text'), text: z.string()}),
                      z.object({kind: z.literal('reasoning'), text: z.string()}),
                      z.object({
                        kind: z.literal('tool_call'),
                        toolName: z.string(),
                        input: z.unknown()
                      })
                    ])
                  )
                  .describe('Typed content blocks composing this assistant message'),
                sources: z
                  .array(chatSourceSchema)
                  .nullable()
                  .optional()
                  .describe('Normalized source attributions cited by this assistant message')
              }),
              z.object({
                role: z.literal('tool'),
                content: z.string().describe('The content of the message'),
                name: z.string().describe('The name of the tool')
              })
            ])
          ),
          summary: z
            .string()
            .nullable()
            .optional()
            .describe('Summary of conversation history when messages have been compressed')
        }),
        403: z.object({
          message: z.string()
        }),
        404: z.object({
          message: z.string()
        })
      }
    },
    count: {
      method: 'GET',
      path: '/count',
      responses: {
        200: z.object({
          count: z.number().describe('The total number of chat conversations for the user')
        })
      },
      summary: 'Get chat conversation count',
      description: 'Get the total number of chat conversations for the current user'
    },
    update: {
      method: 'PATCH',
      path: '/:id',
      pathParams: DB_SCHEMAS.selectChatConversation.pick({id: true}),
      body: z.object({
        title: z.string().trim().min(1, 'Title cannot be empty').max(255, 'Title must be 255 characters or fewer')
      }),
      responses: {
        200: DB_SCHEMAS.selectChatConversation,
        400: z.object({message: z.string()}),
        403: z.object({message: z.string()}),
        404: z.object({message: z.string()})
      },
      summary: 'Update a chat conversation',
      description: 'Update editable fields (currently the title) on a chat conversation'
    }
  },
  {
    pathPrefix: '/chat-conversations'
  }
);
